import React, { useState } from 'react';
import { createRoom } from '../utils/api';
import { useToast } from '../context/ToastContext';
import Modal from './Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

const AddRoom = ({ siteId, sites, onRoomAdded }) => {
        const [roomName, setRoomName] = useState('');
        const [selectedSiteId, setSelectedSiteId] = useState('');
        const [error, setError] = useState(null);
        const [isAdding, setIsAdding] = useState(false);
        const { showToast } = useToast();

        // In the "All Sites" view no single site is active, so the caller passes the
        // candidates instead and picking one becomes part of the form. A fixed
        // siteId always wins: when the user is already looking at one site, asking
        // again would be noise.
        const needsSiteChoice = !siteId && (sites?.length || 0) > 0;

        // Nothing to add to, and no way to ask which — render nothing rather than a
        // button that can only fail.
        if (!siteId && !needsSiteChoice) {
                return null;
        }

        const handleSubmit = async (e) => {
                e.preventDefault();
                setError(null);

                if (!roomName.trim()) {
                        setError('Room name is required');
                        return;
                }

                if (needsSiteChoice && !selectedSiteId) {
                        setError('Site is required');
                        return;
                }

                try {
                        const response = await createRoom({
                                name: roomName,
                                site_id: siteId || parseInt(selectedSiteId)
                        });

                        if (!response.ok) {
                                throw new Error('Failed to create room');
                        }

                        const newRoom = await response.json();
                        onRoomAdded(newRoom.data);
                        setRoomName('');
                        setSelectedSiteId('');
                        setIsAdding(false);
                        showToast('success', 'Room created successfully');
                } catch (err) {
                        setError(err.message);
                        showToast('error', 'Failed to create room');
                }
        };

        return (
                <>
                        <button
                                onClick={() => setIsAdding(true)}
                                data-testid="add-room-open"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-brand text-white font-medium rounded-xl hover:bg-brand/90 transition-colors"
                        >
                                <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
                                Add room
                        </button>
                        <Modal isOpen={isAdding} onClose={() => setIsAdding(false)}>
                                <form onSubmit={handleSubmit}>
                                        <h3 className="text-lg font-bold mb-4">Add a new room</h3>
                                        <div className="space-y-3">
                                                <input
                                                        type="text"
                                                        value={roomName}
                                                        onChange={(e) => setRoomName(e.target.value)}
                                                        placeholder="Enter room name"
                                                        data-testid="add-room-name"
                                                        className="w-full p-2 border rounded-md"
                                                />
                                                {needsSiteChoice && (
                                                        <div>
                                                                <label htmlFor="add-room-site" className="block text-sm text-gray-600 mb-1">Site</label>
                                                                <select
                                                                        id="add-room-site"
                                                                        data-testid="add-room-site"
                                                                        value={selectedSiteId}
                                                                        onChange={(e) => setSelectedSiteId(e.target.value)}
                                                                        className="w-full p-2 border rounded-md"
                                                                >
                                                                        <option value="">Select a site</option>
                                                                        {sites.map(site => (
                                                                                <option key={site.id} value={site.id}>{site.name}</option>
                                                                        ))}
                                                                </select>
                                                        </div>
                                                )}
                                                <button type="submit" data-testid="add-room-submit" className="w-full px-4 py-2 bg-brand text-white rounded-md hover:bg-brand/90">
                                                        Create room
                                                </button>
                                        </div>
                                        {error && <p data-testid="add-room-error" className="text-red-500 mt-2">{error}</p>}
                                </form>
                        </Modal>
                </>
        );
};

export default AddRoom;
